/** Vetores 2D. Imutaveis, sem alocacao escondida. */

export interface Vec2 {
  readonly x: number;
  readonly y: number;
}

export const v = (x: number, y: number): Vec2 => ({ x, y });
export const ZERO: Vec2 = { x: 0, y: 0 };

export const add = (a: Vec2, b: Vec2): Vec2 => ({ x: a.x + b.x, y: a.y + b.y });
export const sub = (a: Vec2, b: Vec2): Vec2 => ({ x: a.x - b.x, y: a.y - b.y });
export const scale = (a: Vec2, k: number): Vec2 => ({ x: a.x * k, y: a.y * k });
export const neg = (a: Vec2): Vec2 => ({ x: -a.x, y: -a.y });

export const dot = (a: Vec2, b: Vec2): number => a.x * b.x + a.y * b.y;
/** Produto vetorial escalar (componente z). Sinal = orientacao. */
export const cross = (a: Vec2, b: Vec2): number => a.x * b.y - a.y * b.x;

export const len = (a: Vec2): number => Math.hypot(a.x, a.y);
export const dist = (a: Vec2, b: Vec2): number => Math.hypot(a.x - b.x, a.y - b.y);
export function normalize(a: Vec2): Vec2 {
  const l = len(a);
  if (l < 1e-12) return ZERO;
  return { x: a.x / l, y: a.y / l };
}

/** Rotacao de 90 graus no sentido anti-horario. */
export const perp = (a: Vec2): Vec2 => ({ x: -a.y, y: a.x });

export function rotate(a: Vec2, rad: number): Vec2 {
  const c = Math.cos(rad);
  const s = Math.sin(rad);
  return { x: a.x * c - a.y * s, y: a.x * s + a.y * c };
}

export const lerp = (a: Vec2, b: Vec2, t: number): Vec2 => ({
  x: a.x + (b.x - a.x) * t,
  y: a.y + (b.y - a.y) * t,
});

export const midpoint = (a: Vec2, b: Vec2): Vec2 => lerp(a, b, 0.5);

/** Direcao de `a` em radianos, em (-pi, pi]. */
export const angleOf = (a: Vec2): number => Math.atan2(a.y, a.x);

export const fromAngle = (rad: number, r = 1): Vec2 => ({
  x: r * Math.cos(rad),
  y: r * Math.sin(rad),
});

/**
 * Angulo assinado de `a` para `b`, em (-pi, pi].
 *
 * Via `atan2(cross, dot)` -- numericamente estavel perto de 0 e perto de pi, ao
 * contrario de `acos(dot / (|a| |b|))`, que perde precisao justamente nos casos
 * degenerados que mais importam para a correcao geometrica.
 */
export function angleBetween(a: Vec2, b: Vec2): number {
  return Math.atan2(cross(a, b), dot(a, b));
}

/** Angulo nao-assinado entre duas direcoes, em [0, pi]. */
export function angleAbs(a: Vec2, b: Vec2): number {
  return Math.abs(angleBetween(a, b));
}

export const toDeg = (rad: number): number => (rad * 180) / Math.PI;
export const toRad = (deg: number): number => (deg * Math.PI) / 180;
